import { useEffect, useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, Building2, CalendarDays, Newspaper, Users, FileText, HeartHandshake, Gift, ArrowLeft, Clock, CheckCircle2, PauseCircle, XCircle, Facebook, Share2, Sparkles, PlusCircle, SmartphoneNfc } from "lucide-react";

const LINKS = [
  { to: "/espace-association", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "assoc-nav-dashboard" },
  { to: "/espace-association/assistant-ia", icon: Sparkles, label: "Mon Agent IA", testid: "assoc-nav-ai", highlight: true },
  { to: "/ma-carte", icon: SmartphoneNfc, label: "Ma carte de visite digitale", testid: "assoc-nav-carte" },
  { to: "/espace-association/profil", icon: Building2, label: "Ma page vitrine", testid: "assoc-nav-profile" },
  { to: "/espace-association/evenements", icon: CalendarDays, label: "Mes événements", testid: "assoc-nav-events" },
  { to: "/espace-association/actualites", icon: Newspaper, label: "Mes actualités", testid: "assoc-nav-news" },
  { to: "/petites-annonces/deposer", icon: PlusCircle, label: "Déposer une petite annonce", testid: "assoc-nav-new-annonce" },
  { to: "/entre-voisins", icon: HeartHandshake, label: "Publier une entraide / un service", testid: "assoc-nav-new-voisin" },
  { to: "/demande-de-devis", icon: FileText, label: "Faire une demande de devis", testid: "assoc-nav-new-quote" },
  { to: "/espace-association/publications-facebook", icon: Facebook, label: "Publier sur Facebook", testid: "assoc-nav-fbposts" },
  { to: "/espace-association/partager-evenement", icon: Share2, label: "Partager un événement", testid: "assoc-nav-event-share" },
  { to: "/espace-association/partager-article", icon: Newspaper, label: "Partager un article de blog", testid: "assoc-nav-blog-share" },
  { to: "/espace-association/partager-actualite", icon: Newspaper, label: "Partager un article d'actualité", testid: "assoc-nav-press-share" },
  { to: "/espace-association/partager-entre-voisins", icon: Share2, label: "Partager une entraide Entre Voisins", testid: "assoc-nav-voisin-share" },
  { to: "/espace-association/membres", icon: Users, label: "Mes membres", testid: "assoc-nav-members" },
  { to: "/espace-association/dons", icon: Gift, label: "Mes dons", testid: "assoc-nav-donations" },
  { to: "/espace-association/documents", icon: FileText, label: "Mes documents", testid: "assoc-nav-documents" },
  { to: "/espace-association/ressources", icon: HeartHandshake, label: "Ressources", testid: "assoc-nav-resources" },
];

export default function AssocLayout() {
  const [asso, setAsso] = useState(null);
  useEffect(() => { api.get("/associations/me").then((r) => setAsso(r.data.association)).catch(() => {}); }, []);
  const link = ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm leading-tight ${isActive ? "bg-emerald-700 text-white" : "hover:bg-slate-100 text-slate-700"}`;
  const statusMeta = (() => {
    if (!asso) return null;
    if (asso.status === "active") return { Icon: CheckCircle2, label: "Page publiée", cls: "text-emerald-700" };
    if (asso.status === "suspended") return { Icon: PauseCircle, label: "Page suspendue", cls: "text-slate-500" };
    if (asso.status === "rejected") return { Icon: XCircle, label: "Demande refusée", cls: "text-rose-600" };
    return { Icon: Clock, label: "En attente de validation", cls: "text-amber-600" };
  })();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-white border-b md:border-b-0 md:border-r border-slate-200 p-4 md:flex md:flex-col shrink-0">
        <Link to="/" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4 md:mb-6"><ArrowLeft size={14} /> Retour au site</Link>
        <div className="font-heading font-extrabold text-lg mb-1 truncate">{asso?.name || "Mon association"}</div>
        {statusMeta && (
          <div className={`text-xs font-bold uppercase tracking-widest mb-4 flex items-center gap-1 ${statusMeta.cls}`} data-testid="assoc-status-label">
            <statusMeta.Icon size={12} /> {statusMeta.label}
          </div>
        )}
        <nav className="flex md:flex-col gap-1 overflow-x-auto md:overflow-visible pb-1">
          {LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testid}
              className={l.highlight
                ? ({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap ${isActive ? "bg-[#E05638] text-white" : "text-[#E05638] hover:bg-orange-50"}`
                : link}>
              <l.icon size={16} className="shrink-0" /> {l.label}
            </NavLink>
          ))}
        </nav>
        {asso?.status === "active" && asso?.slug && (
          <a href={`/associations/${asso.slug}`} target="_blank" rel="noopener noreferrer"
             className="mt-4 hidden md:inline-block text-xs text-emerald-700 font-semibold hover:underline" data-testid="assoc-view-public">
            Voir ma page publique →
          </a>
        )}
      </aside>
      <main className="flex-1 min-w-0 p-4 md:p-10 overflow-x-auto"><Outlet context={{ asso, setAsso }} /></main>
    </div>
  );
}
